import { SetupComponentAgingComponent } from './../../components/setup-component-aging/setup-component-aging.component';
import { LoadAnticipatedExpendituresAction, SetupFiscalYearAction, SkipFiscalYearSetupAction } from './../../setup-store/setup.actions';
import { SetSelectedFiscalYearAction, UpdateFiscalYearsAction } from 'app/internal/property/property-store/property.actions';
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { getSelectedProperty, getSelectedFiscalYear } from '../../../property/property-store/property.selectors';
import { AppState } from '../../../../app.reducers';
import { getAnticipatedExpenditures, getSetupSaved } from '../../setup-store/setup.selectors';
import { Property } from '../../../property/shared/models';
import { PropertyComponentGroup } from '../../../plans/shared/models';
import { SubSink } from 'subsink';
import { InformationSource } from '../../../component/models';
import { getComponentInformationSourcesLookup } from '../../../component/component-store/component.selectors';
import { ModalComponent, FiscalYear } from 'app/internal/shared';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ReviewAnticipatedExpendituresComponent } from '../../components';

@Component({
  templateUrl: './setup-fiscal-year.component.html',
  styleUrls: ['./setup-fiscal-year.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class SetupFiscalYearComponent implements OnInit, OnDestroy {

  @ViewChild(ModalComponent, { static: true }) public skipEverythingModal: ModalComponent;
  @ViewChild(ReviewAnticipatedExpendituresComponent) anticipatedExpendituresComponent: ReviewAnticipatedExpendituresComponent;
  @ViewChild(SetupComponentAgingComponent) componentAgingComponent: SetupComponentAgingComponent;

  selectedFiscalYear: number;
  previousFiscalYear: FiscalYear;
  propertyId: string;
  showAdvancedOptions = false;
  hasSubmitted = false;
  skippedSetup = false;
  currentStep = 1;

  anticipatedExpenditures$: Observable<PropertyComponentGroup>;
  property$: Observable<Property>;
  selectedFiscalYear$: Observable<FiscalYear>;
  informationSources$: Observable<InformationSource[]>;
  setupSaved$: Observable<string>;

  private fiscalYearOptions: FiscalYear[];
  private subs = new SubSink();

  constructor(private store: Store<AppState>, private router: Router) {
    this.property$ = this.store.pipe(select(getSelectedProperty));
    this.selectedFiscalYear$ = this.store.pipe(select(getSelectedFiscalYear));
    this.anticipatedExpenditures$ = this.store.pipe(select(getAnticipatedExpenditures));
    this.informationSources$ = this.store.pipe(select(getComponentInformationSourcesLookup));
    this.setupSaved$ = this.store.pipe(select(getSetupSaved));
  }

  ngOnInit() {

    this.subs.sink = combineLatest(this.selectedFiscalYear$, this.property$)
      .pipe(filter(([fy, p]) => fy != null && p != null))
      .subscribe(([fiscalYear, property]) => {
        if (this.propertyId != null && this.propertyId !== property.propertyId) {
          // property changed, no need to load this information because we'll be redirected to the dashboard.
          return;
        }

        // if the year doesn't need to be setup, then redirect to dashboard
        if (!this.hasSubmitted && !fiscalYear.requireSetup) {
          this.router.navigate(['/useraccount/dashboard']);
          return;
        }

        this.propertyId = property.propertyId;
        this.selectedFiscalYear = fiscalYear.year;
        this.previousFiscalYear = this.getFiscalYear(property.fiscalYears, fiscalYear.year - 1);
        this.fiscalYearOptions = property.fiscalYears;

        const payload = { propertyId: this.propertyId, fiscalYear: this.selectedFiscalYear - 1 };
        this.store.dispatch(new LoadAnticipatedExpendituresAction(payload));
      });

    this.subs.sink = this.setupSaved$
      .subscribe((saved) => {
        if (this.hasSubmitted && saved === 'saved') {
          this.setFiscalYearSetupCompleted();
          if (this.skippedSetup === true) {
            this.router.navigateByUrl('/useraccount/plans');
          } else {
            this.currentStep = 3;
          }
        }
      });
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  getFiscalYear(fiscalYears: FiscalYear[], year: number): FiscalYear {
    return fiscalYears.find(x => x.year === year);
  }

  goToStep(step: number) {
    if (step > 2) {
      return;
    }
    this.currentStep = step;
  }

  cancel() {
    this.store.dispatch(new SetSelectedFiscalYearAction(this.previousFiscalYear));
    this.router.navigate(['/useraccount/dashboard']);
  }

  onCompleteSetup() {
    const setupRequest = this.componentAgingComponent.getFinancialDetails();
    setupRequest.extendLifeComponentIds = this.anticipatedExpendituresComponent.getExtendedLifeComponents();
    setupRequest.updateCurrentYear = this.anticipatedExpendituresComponent.getUpdateCurrentYear();

    setupRequest.fiscalYear = this.selectedFiscalYear;
    setupRequest.propertyId = this.propertyId;

    this.store.dispatch(new SetupFiscalYearAction(setupRequest));
    this.hasSubmitted = true;
  }

  setFiscalYearSetupCompleted() {
    this.fiscalYearOptions.forEach(fy => {
      if (fy.year === this.selectedFiscalYear) {
        fy.requireSetup = false;
      }
    });
    this.store.dispatch(new UpdateFiscalYearsAction(this.fiscalYearOptions));
  }

  onToggleAdvancedOptions() {
    this.showAdvancedOptions = !this.showAdvancedOptions;
  }

  onSkipEverything() {
    this.skipEverythingModal.show();
  }

  onConfirmSkipEverything() {
    const payload = { propertyId: this.propertyId, fiscalYear: this.selectedFiscalYear, informationSourceId: null };
    this.store.dispatch(new SkipFiscalYearSetupAction(payload));
    // this.setFiscalYearSetupCompleted();
    this.hasSubmitted = true;
    this.skippedSetup = true;
  }
}
